import React, { useEffect, useState } from "react";
import { Box } from "@mui/system";
import { usePrinterContext } from "../../../../../context/printer";
import { cloneDeep, isEqual } from "lodash";
import { ColorInput, NumberCount } from "../../../../UI/CommonComponents";
import { BORDER_STYLES } from "../../../../../const/general";
import { MenuItem, Select } from "@mui/material";
import { SelectRoot } from "../style";
import Icon from "../../../../UI/Icon";

export default function PaperBorder({ pageIndex }) {
    const {
        pageBlocks,
        setPageBlocks,
        sectionBlock,
        setSectionBlock,
        undoRedoState,
        pageBlocksIndex,
        pageBlocksArray
    } = usePrinterContext();

    const updatedPageBlocks = [...pageBlocks];
    const pageToUpdate = updatedPageBlocks[pageIndex];

    const pageStyles = pageToUpdate?.page_properties;

    const [dependencyChanged, setDependencyChanged] = useState(false);
    const [borderColor, setBorderColor] = useState(
        pageStyles?.border?.color || "#000000"
    );
    const [borderWidth, setBorderWidth] = useState(
        pageStyles?.border?.width || 1
    );
    const [border, setBorder] = useState(
        pageStyles?.border?.position || {
            top: false,
            right: false,
            bottom: false,
            left: false
        }
    );
    const [borderStyle, setBorderStyle] = useState(pageStyles?.border?.style);
    const [topRadius, setTopRadius] = useState(pageStyles?.border?.radius?.top);
    const [bottomRadius, setBottomRadius] = useState(
        pageStyles?.border?.radius?.bottom
    );
    const [rightRadius, setRightRadius] = useState(
        pageStyles?.border?.radius?.right
    );
    const [leftRadius, setLeftRadius] = useState(
        pageStyles?.border?.radius?.left
    );

    useEffect(() => {
        if (undoRedoState?.state && isEqual(pageIndex, sectionBlock.data)) {
            return;
        }
        setBorderStyle(pageStyles?.border?.style);
        setTopRadius(pageStyles?.border?.radius?.top);
        setLeftRadius(pageStyles?.border?.radius?.left);
        setRightRadius(pageStyles?.border?.radius?.right);
        setBottomRadius(pageStyles?.border?.radius?.bottom);
        setBorderColor(pageStyles?.border?.color || "#000000");
        setBorderWidth(pageStyles?.border?.width || 1);
        setBorder(
            pageStyles?.border?.position || {
                top: false,
                right: false,
                bottom: false,
                left: false
            }
        );
        setSectionBlock({ data: pageIndex, pageBlocks: pageBlocks });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageIndex, undoRedoState]);

    useEffect(() => {
        const clonedPageBlocksArray = cloneDeep(pageBlocksArray);
        const historyBlocks = clonedPageBlocksArray[pageBlocksIndex.current];

        if (isEqual(pageBlocks, historyBlocks) && !dependencyChanged) {
            return;
        }

        const newStyles = {
            position: {
                ...border
            },
            width: borderWidth,
            color: borderColor,
            style: borderStyle,
            radius: {
                top: topRadius,
                bottom: bottomRadius,
                left: leftRadius,
                right: rightRadius
            }
        };

        const updatedPageBlocks = [...pageBlocks];
        const pageToUpdate = updatedPageBlocks[pageIndex];

        if (pageToUpdate) {
            pageToUpdate.page_properties.border = {
                ...pageToUpdate?.page_properties?.border,
                ...newStyles
            };

            // Update the state with the modified pageBlocks
            setPageBlocks(updatedPageBlocks);
        }
        setSectionBlock({
            pageBlocks: updatedPageBlocks
        });
        setDependencyChanged(false);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        border,
        borderWidth,
        borderColor,
        borderStyle,
        topRadius,
        bottomRadius,
        leftRadius,
        rightRadius
    ]);

    const updateBorder = (border) => {
        setBorder((prevBorder) => ({
            ...prevBorder,
            [border]: !prevBorder[border] // Toggle the border value
        }));
    };

    const handleStyleChange = (value) => {
        setDependencyChanged(true);
        setBorderStyle(value);
    };

    return (
        <Box className="pageBorderWrapper paperBorder">
            <Box className="borderStyles flexBox alignCenter justifyBetween">
                <Box className="fontLabel mb-0">Borders</Box>
                <Box className="flexBox alignCenter">
                    <span
                        className={`borderBlock top flexBox alignCenter justifyCenter ${
                            border?.top ? "active" : ""
                        }`}
                        onClick={() => {
                            updateBorder("top");
                            setDependencyChanged(true);
                        }}
                    ></span>
                    <span
                        className={`borderBlock right flexBox alignCenter justifyCenter ${
                            border?.right ? "active" : ""
                        }`}
                        onClick={() => {
                            updateBorder("right");
                            setDependencyChanged(true);
                        }}
                    ></span>
                    <span
                        className={`borderBlock bottom flexBox alignCenter justifyCenter ${
                            border?.bottom ? "active" : ""
                        }`}
                        onClick={() => {
                            updateBorder("bottom");
                            setDependencyChanged(true);
                        }}
                    ></span>
                    <span
                        className={`borderBlock left flexBox alignCenter justifyCenter ${
                            border?.left ? "active" : ""
                        }`}
                        onClick={() => {
                            updateBorder("left");
                            setDependencyChanged(true);
                        }}
                    ></span>
                </Box>
            </Box>
            <Box className="flexBox alignEnd justifyBetween">
                <Box className="flex1 borderWidth">
                    <Box className="fontLabel mt-0">
                        Style / Thickness / Color
                    </Box>
                    <Box className="flexBox">
                        <Box sx={{ width: 118 }}>
                            <SelectRoot size="small" sx={{ width: "100%" }}>
                                <Select
                                    labelId="input-label"
                                    id="input-select"
                                    value={borderStyle}
                                    onChange={(e) =>
                                        handleStyleChange(e.target.value)
                                    }
                                    className="input-select"
                                    MenuProps={{
                                        classes: {
                                            paper: "customSelectList"
                                        }
                                    }}
                                >
                                    {BORDER_STYLES?.map((position, index) => (
                                        <MenuItem value={position} key={index}>
                                            {position}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </SelectRoot>
                        </Box>
                        <NumberCount
                            setNumberCount={setBorderWidth}
                            numberCount={borderWidth}
                            setDependencyChanged={setDependencyChanged}
                        />
                        <ColorInput
                            value={borderColor}
                            setValue={setBorderColor}
                            setDependencyChanged={setDependencyChanged}
                        />
                    </Box>
                </Box>
            </Box>
            <Box className="pageBorderCorners" sx={{ mt: 2, mb: 2.5 }}>
                <Box className="blockTitle" sx={{ mb: 1 }}>
                    <span className="mediumFont">Round Corners</span>
                </Box>
                <Box className="flexBox justifyAround alignCenter alignmentsText borderCorners">
                    <Icon name="topLeftBorder" />
                    <Icon name="topRightBorder" />
                    <Icon name="bottomRightBorder" />
                    <Icon name="bottomLeftBorder" />
                </Box>
                <Box className="containerPadding flexBox alignCenter justifyCenter">
                    <NumberCount
                        setNumberCount={setTopRadius}
                        numberCount={topRadius}
                        setDependencyChanged={setDependencyChanged}
                    />
                    <NumberCount
                        setNumberCount={setRightRadius}
                        numberCount={rightRadius}
                        setDependencyChanged={setDependencyChanged}
                    />
                    <NumberCount
                        setNumberCount={setBottomRadius}
                        numberCount={bottomRadius}
                        setDependencyChanged={setDependencyChanged}
                    />
                    <NumberCount
                        setNumberCount={setLeftRadius}
                        numberCount={leftRadius}
                        setDependencyChanged={setDependencyChanged}
                    />
                </Box>
            </Box>
        </Box>
    );
}
